import React, { useState } from "react";
import PanelState from "../MainComponents/PanelState";

/**
 * Task 37, B3 — attaching each customer to a ConnectWise company and agreement, and saying who owns
 * their pricing.
 *
 * Presentational. It renders what it is given and reports changes back; whether a mapping may be
 * saved, and whether an agreement may be changed, are both decided by the server.
 *
 * NO MODAL. Editing happens in the row, as the client asked — a dialog that vanishes when somebody
 * clicks beside it is the pattern we were explicitly told to move away from.
 */
/**
 * Whether the row being edited still says exactly what it said before.
 *
 * Opening an already-attached customer seeded the draft from the mapping, so Save was enabled the
 * instant the row opened, before anything had been changed — which read as though an edit had been
 * made. A save of identical values is also a write nobody asked for.
 */
function unchanged(customer, draft) {
    const same = (a, b) => (a || null) === (b || null);
    return same(customer.cwCompanyId, draft.cwCompanyId)
        && same(customer.cwAgreementId, draft.cwAgreementId)
        && (customer.pricingMode || "cloudcover") === (draft.pricingMode || "cloudcover");
}

export default function CustomerAgreementGrid({
    customers,
    companies,
    agreements,
    loadingAgreements,
    editingCustomerId,
    draft,
    saving,
    onBeginEdit,
    onCancelEdit,
    onDraftChange,
    onCompanyChosen,
    onSave,
    loading,
    error,
    onRetry,
    // A master administrator working on another reseller sees the mappings but cannot change them;
    // the server refuses the save. No Change / Set up button is offered.
    readOnly,
}) {
    const [filter, setFilter] = useState("");

    const needle = filter.trim().toLowerCase();
    const visible = needle
        ? customers.filter((c) => (c.customerName || "").toLowerCase().includes(needle))
        : customers;

    return (
        <div>
            <p className="text-muted" style={{ maxWidth: 820 }}>
                {readOnly
                    ? "How this reseller's customers are attached to their ConnectWise agreements. Only the reseller can change these."
                    : "Attach each customer to one of your ConnectWise agreements. Pricing can come from CloudCover or from ConnectWise — choose ConnectWise where their agreement already carries the price you want to bill."}
            </p>

            <div className="mb-3" style={{ maxWidth: 320 }}>
                <input
                    type="text"
                    className="form-control"
                    placeholder="Search customers"
                    value={filter}
                    onChange={(e) => setFilter(e.target.value)}
                />
            </div>

            {visible.length === 0 ? (
                // Three outcomes, not one. An empty list used to mean "no customers" whether the
                // fetch had succeeded, failed or not finished — and a Retry is only worth offering
                // for the one of those it can do anything about.
                <PanelState
                    busy={loading}
                    error={error}
                    title={customers.length === 0 ? "You have no customers yet." : "No customers match that search."}
                    detail={customers.length === 0
                        ? "Customers appear here once they are set up on your account."
                        : "Try a shorter search."}
                    actionLabel={error ? "Try again" : undefined}
                    busyLabel="Trying…"
                    onAction={error ? onRetry : undefined}
                />
            ) : (
                <table className="table table-sm align-middle">
                    <thead>
                        <tr>
                            <th>Customer</th>
                            <th>Status</th>
                            <th>Company</th>
                            <th>Agreement</th>
                            <th>Pricing</th>
                            <th style={{ width: 150 }} />
                        </tr>
                    </thead>
                    <tbody>
                        {visible.map((c) => {
                            const isEditing = editingCustomerId === c.customerOrgId;

                            if (!isEditing) {
                                return (
                                    <tr key={c.customerOrgId}>
                                        <td>{c.customerName}</td>
                                        <td>
                                            <span className="text-muted" style={{ fontSize: 13 }}>
                                                {c.status || "—"}
                                            </span>
                                        </td>
                                        <td>{c.cwCompanyName || <span className="text-muted">Not set</span>}</td>
                                        <td>{c.cwAgreementName || <span className="text-muted">Not set</span>}</td>
                                        <td>
                                            {c.pricingMode === "connectwise" ? "ConnectWise" : "CloudCover"}
                                        </td>
                                        <td className="text-end">
                                            {!readOnly && (
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-primary"
                                                    disabled={saving}
                                                    onClick={() => onBeginEdit(c)}
                                                >
                                                    {c.cwAgreementId ? "Change" : "Set up"}
                                                </button>
                                            )}
                                        </td>
                                    </tr>
                                );
                            }

                            return (
                                <tr key={c.customerOrgId} className="table-light">
                                    <td>{c.customerName}</td>
                                    <td>
                                        <span className="text-muted" style={{ fontSize: 13 }}>
                                            {c.status || "—"}
                                        </span>
                                    </td>
                                    <td>
                                        <select
                                            className="form-select form-select-sm"
                                            value={draft.cwCompanyId || ""}
                                            disabled={saving || c.hasActiveLine}
                                            onChange={(e) => onCompanyChosen(Number(e.target.value) || null)}
                                        >
                                            <option value="">Choose…</option>
                                            {companies.map((co) => (
                                                <option key={co.id} value={co.id}>{co.name}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td>
                                        <select
                                            className="form-select form-select-sm"
                                            value={draft.cwAgreementId || ""}
                                            disabled={saving || c.hasActiveLine || !draft.cwCompanyId || loadingAgreements}
                                            onChange={(e) => {
                                                const id = Number(e.target.value) || null;
                                                const chosen = agreements.find((a) => a.id === id);
                                                onDraftChange({
                                                    ...draft,
                                                    cwAgreementId: id,
                                                    cwAgreementName: chosen ? chosen.name : null,
                                                });
                                            }}
                                        >
                                            <option value="">
                                                {loadingAgreements ? "Loading…" : "Choose…"}
                                            </option>
                                            {agreements.map((a) => (
                                                <option key={a.id} value={a.id}>{a.name}</option>
                                            ))}
                                        </select>
                                    </td>
                                    <td>
                                        <select
                                            className="form-select form-select-sm"
                                            value={draft.pricingMode || "cloudcover"}
                                            disabled={saving}
                                            onChange={(e) => onDraftChange({ ...draft, pricingMode: e.target.value })}
                                        >
                                            <option value="cloudcover">CloudCover</option>
                                            <option value="connectwise">ConnectWise</option>
                                        </select>
                                    </td>
                                    <td className="text-end">
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary me-1"
                                            disabled={saving || !draft.cwCompanyId || !draft.cwAgreementId || unchanged(c, draft)}
                                            onClick={onSave}
                                        >
                                            {saving ? "Saving…" : "Save"}
                                        </button>
                                        <button
                                            type="button"
                                            className="btn btn-sm btn-primary"
                                            disabled={saving}
                                            onClick={onCancelEdit}
                                        >
                                            Cancel
                                        </button>
                                    </td>
                                </tr>
                            );
                        })}
                    </tbody>
                </table>
            )}

            {visible.some((c) => c.hasActiveLine) && (
                <p className="text-muted" style={{ fontSize: 13, maxWidth: 820 }}>
                    Customers with billing already on an agreement cannot be moved to a different one
                    while those lines are open. Once they have ended, the agreement can be changed —
                    which is what you would do at a contract renewal.
                </p>
            )}
        </div>
    );
}
